import { useEffect, useMemo, useState } from "react";

import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MedfarDictionariesValidationRuleTypeEnum } from "@/Common/Models/MedfarDictionaries/Enums/MedfarDictionariesValidationRuleTypeEnum";
import { ContactDirectoryHelper } from "@/ContactDirectory/Helpers/ContactDirectoryHelper";
import { useDefaultDirectoryCountryAndState } from "@/ContactDirectory/Hooks/useDefaultDirectoryCountryAndState";
import { useMedfarDictionariesValidationRule } from "@/ContactDirectory/Hooks/useMedfarDictionariesValidationRule";

import styles from "@/ContactDirectory/Components/CommonVariables.module.scss";

interface IFaxDirectoryManualEntry {
    faxNumber: string;
    patientContactId: string;
    isForPharmacy: boolean;
    setSelectedFaxNumber: (faxNumber: string, countryId: string, provinceId: string) => void;
    setValid?: (isValid: boolean) => void;
    showProvinceField?: boolean;
}

export function FaxDirectoryManualEntry(props: IFaxDirectoryManualEntry): JSX.Element {
    const { getText } = useTranslation();
    const countries = useMemo(
        () => ContactDirectoryHelper.getSupportedCountries(),
        []
    );

    const { defaultCountry, defaultState } = useDefaultDirectoryCountryAndState(props.patientContactId, props.isForPharmacy);
    const [selectedCountryId, setSelectedCountryId] = useState<string>(defaultCountry.Id);
    const [selectedProvinceId, setSelectedProvinceId] = useState<string>(defaultState.Id);

    const [faxNumber, setFaxNumber] = useState(props.faxNumber ?? "");

    const phoneNumberValidationRule = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PhoneNumber, selectedCountryId, selectedProvinceId);

    const phoneRegex = useMemo(
        () => phoneNumberValidationRule?.ValidationRegexPattern ? new RegExp(phoneNumberValidationRule.ValidationRegexPattern, "i") : null,
        [phoneNumberValidationRule?.ValidationRegexPattern]
    );

    const isFaxValid: boolean = useMemo(
        () => phoneRegex?.test(faxNumber),
        [faxNumber, phoneRegex]
    );

    useEffect(
        () => {
            if (props.faxNumber !== faxNumber)
                setFaxNumber(props.faxNumber);
        },
        [props.faxNumber]
    );

    useEffect(
        () => props.setValid?.(isFaxValid),
        [isFaxValid]
    );

    useEffect(
        () => {
            props.setSelectedFaxNumber(faxNumber, selectedCountryId, selectedProvinceId);
        },
        [faxNumber, selectedCountryId, selectedProvinceId]
    );
    return (
        <>
            <div className="margin-xs-top bold">{getText("CountryLabel")}</div>
            <ComboBoxControl
                className={styles.widthComboBox}
                id="_FaxCountryComboBox"
                items={countries.map(x => new ComboBoxDropDownItem(x.Id, x.Name)).toImmutable()}
                selectedCode={selectedCountryId}
                onChange={country => {
                    setSelectedCountryId(country);
                    setSelectedProvinceId("");
                }}
            />
            {props.showProvinceField && (
                <>
                    <div className="margin-xs-top bold">{getText("ProvinceLabel")}</div>
                    <ComboBoxControl
                        className={styles.widthComboBox}
                        id="_FaxProvinceComboBox"
                        items={cacheStore.GetGeographicsStatesForCountryId(selectedCountryId)
                            .map(x => new ComboBoxDropDownItem(x.Id, x.Name)).toImmutable()
                        }
                        selectedCode={selectedProvinceId}
                        onChange={province => setSelectedProvinceId(province)}
                    />
                </>
            )}
            <div className="margin-xs-top bold">{getText("FaxNumberLabel")}</div>
            <TextboxMaskedControl
                className={styles.widthComboBox}
                allowEmpty={false}
                isValid={isFaxValid}
                id="manually-enter-fax-number"
                value={faxNumber}
                onChangeFunction={faxNumber => setFaxNumber(faxNumber)}
                mask={phoneNumberValidationRule?.EditMaskPattern}
            />
        </>
    );
}
